import { FC, useState } from "react";
import { AgentAction } from "../../types";
import { Markdown, Stack } from "@altimateai/lego";
import { Artifacts } from "../artifacts";
import {
  ChainOfThought,
  ChainOfThoughtHeader,
  ChainOfThoughtContent,
  ChainOfThoughtStep,
} from "@altimateai/lego";
import {
  IconSearch,
  IconMail,
  IconCode,
  IconDatabase,
  IconBrain,
  IconLoader2,
} from "@tabler/icons-react";
import { cn } from "../../../../shared/utils";

interface StatusUpdatesProps {
  actions: AgentAction[];
  isComplete?: boolean;
  heading: string;
}

// Helper function to detect icon component from content
const getIconComponentForAction = (content: string) => {
  const lowerContent = content.toLowerCase();

  if (lowerContent.includes("search") || lowerContent.includes("finding")) {
    return IconSearch;
  }
  if (lowerContent.includes("mail") || lowerContent.includes("email")) {
    return IconMail;
  }
  if (
    lowerContent.includes("code") ||
    lowerContent.includes("query") ||
    lowerContent.includes("sql")
  ) {
    return IconCode;
  }
  if (lowerContent.includes("database") || lowerContent.includes("table")) {
    return IconDatabase;
  }
  // Default icon for generic steps
  return IconBrain;
};

// Helper function to render content with URL pills
const renderContentWithURLPills = (content: string) => {
  // Match URLs in the content
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(urlRegex);

  return (
    <span>
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          try {
            const hostname = new URL(part).hostname;
            return (
              <span
                key={index}
                className="al-inline-block al-bg-blue-100 al-text-blue-700 al-px-2 al-py-0.5 al-rounded-full al-text-xs al-mx-1 al-font-normal"
              >
                {hostname}
              </span>
            );
          } catch {
            // If URL parsing fails, render as text
            return <span key={index}>{part}</span>;
          }
        }
        return (
          <span key={index} className="al-italic">
            <Markdown
              content={part}
              className="al-text-[1em]"
              components={{
                p: ({ className, ...props }) => (
                  <p className={cn("al-text-small", className)} {...props} />
                ),
              }}
            />
          </span>
        );
      })}
    </span>
  );
};

export const StatusUpdates: FC<StatusUpdatesProps> = ({
  actions,
  isComplete = false,
  heading,
}) => {
  const infoActions = actions.filter((a) => a.type === "info");
  const analysisActions = actions.filter((a) => a.type === "analysis");
  const [isOpen, setIsOpen] = useState(!isComplete);

  if (!actions.length) return null;

  return (
    <Stack
      direction="column"
      className="al-mb-4 "
      role="log"
      aria-live="polite"
      aria-label="Action status updates"
    >
      <ChainOfThought
        open={isOpen}
        onOpenChange={setIsOpen}
        style={{ maxWidth: "100%" }}
      >
        <ChainOfThoughtHeader>
          <Stack
            direction="row"
            align="center"
            justify="between"
            className="al-w-full"
          >
            <span>{heading || "Assistant"}</span>
            <span className="al-text-brand al-text-xs">
              {!isComplete ? "Thinking" : "View detailed steps"}
            </span>
          </Stack>
        </ChainOfThoughtHeader>

        <ChainOfThoughtContent>
          {infoActions.map((action, index) => {
            const isActive = !isComplete && index === infoActions.length - 1;
            const hasRichContent =
              !!action.artifacts && action.artifacts.length > 0;
            const IconComponent = getIconComponentForAction(
              action.content || "",
            );

            return (
              <ChainOfThoughtStep
                key={action.id || `action-${index}`}
                icon={isActive ? IconLoader2 : IconComponent}
                status={isActive ? "active" : "complete"}
                label={
                  <div
                    role="status"
                    className={cn(
                      "al-text-muted-foreground al-text-sm",
                      hasRichContent && "al-bg-gray-50 al-p-3 al-rounded-md",
                    )}
                  >
                    {/* Render content with URL pills */}
                    <div className="al-leading-relaxed">
                      {renderContentWithURLPills(action.content || "")}
                    </div>

                    {/* Artifacts with spacing */}
                    {action.artifacts && (
                      <div className="al-mt-3">
                        <Artifacts artifacts={action.artifacts} />
                      </div>
                    )}
                  </div>
                }
              />
            );
          })}
        </ChainOfThoughtContent>
      </ChainOfThought>

      {/* Analysis actions displayed separately (outside collapsible) */}
      {analysisActions.length > 0 && (
        <Stack direction="column" className="al-text-sm al-text-dark al-mt-2">
          {analysisActions.map((action) => (
            <Markdown
              key={action.id}
              content={action.content || ""}
              id={action.id}
            />
          ))}
        </Stack>
      )}

      {/* Processing indicator */}
      {!isComplete && (
        <Stack direction="row" align="center" gap="xs" className="al-mt-2">
          <div
            className="al-animate-spin al-h-4 al-w-4 al-border-2 al-border-primary al-border-t-transparent al-rounded-full"
            aria-hidden="true"
          />
          <span className="al-text-sm al-text-muted-foreground">
            Processing
          </span>
        </Stack>
      )}
    </Stack>
  );
};
